/**
 * NotificationBell — floating top-right bell icon that shows a red badge with
 * unread count. Clicking opens a popover with the user's recent in-app
 * notifications. Currently fed by the `/api/notifications` endpoint which
 * aggregates estimate-view events, incoming SMS replies, and any other
 * `db.notifications` inserts. Auto-refreshes every 60 seconds.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Eye, Mail, MessageSquare, CheckCheck, X, Flame, Clock, Wrench, Calendar } from "lucide-react";
import apiClient from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

const iconFor = (type) => {
  switch (type) {
    case "hot_lead": return <Flame className="w-4 h-4 text-orange-400" />;
    case "estimate_viewed": return <Eye className="w-4 h-4 text-emerald-400" />;
    case "incoming_sms": return <MessageSquare className="w-4 h-4 text-blue-400" />;
    case "payment_received": return <CheckCheck className="w-4 h-4 text-emerald-400" />;
    case "employee_time_logged": return <Clock className="w-4 h-4 text-amber-400" />;
    case "employee_material_added": return <Wrench className="w-4 h-4 text-emerald-400" />;
    case "booking_request": return <Calendar className="w-4 h-4 text-orange-400" />;
    default: return <Mail className="w-4 h-4 text-zinc-400" />;
  }
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/notifications?limit=25");
      setItems(res.data?.notifications || []);
      setUnread(res.data?.unread_count || 0);
    } catch (err) {
      // Silent — bell is non-critical and some employee roles may 403
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // poll every 60s
    return () => clearInterval(t);
  }, [load]);

  const openNotification = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await apiClient.post(`/notifications/${n.id}/read`); load(); } catch (_) {}
    }
    // Route to the related entity when we can
    if ((n.type === "estimate_viewed" || n.type === "hot_lead") && n.estimate_id) {
      navigate(`/estimates?highlight=${n.estimate_id}`);
    } else if (n.type === "incoming_sms") {
      navigate(`/communications`);
    } else if ((n.type === "employee_time_logged" || n.type === "employee_material_added") && n.job_id) {
      // Deep-link to the admin Jobs page with `?highlight=<id>` — JobsPage
      // already watches this param and auto-opens the job detail dialog.
      navigate(`/jobs?highlight=${n.job_id}`);
    } else if (n.type === "booking_request") {
      // New website booking requests land on the calendar — the
      // PendingBookingRequests panel renders above the month grid and lets
      // the admin confirm / propose alternative / decline inline.
      const id = n.online_booking_request_id;
      navigate(id ? `/calendar?booking=${id}` : `/calendar`);
    }
  };

  const markAllRead = async () => {
    try {
      await apiClient.post("/notifications/read-all");
      toast.success("Marked all as read");
      load();
    } catch (err) {
      toast.error("Could not mark all read");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="fixed top-4 right-6 z-40 w-11 h-11 rounded-full bg-zinc-900 border border-zinc-800 hover:border-primary/40 flex items-center justify-center shadow-lg transition"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-zinc-300" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-zinc-900 border-zinc-800" align="end">
        <div className="flex items-center justify-between p-3 border-b border-zinc-800">
          <h3 className="text-sm font-semibold text-white">Notifications</h3>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:underline">Mark all read</button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              <Bell className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
              You're all caught up.
            </div>
          ) : items.map(n => (
            <button
              key={n.id}
              onClick={() => openNotification(n)}
              className={`w-full text-left p-3 border-b border-zinc-800/60 hover:bg-zinc-800/50 transition flex gap-3 ${!n.read ? "bg-zinc-800/30" : ""}`}
              data-testid={`notification-${n.type}`}
            >
              <div className="flex-shrink-0 mt-0.5">{iconFor(n.type)}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm ${!n.read ? "text-white font-medium" : "text-zinc-300"}`}>{n.title}</p>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />}
                </div>
                {n.message && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{n.message}</p>}
                <p className="text-[11px] text-zinc-600 mt-1">{timeAgo(n.created_at)}</p>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
